<script lang="ts" setup>
import type { NavigationLink } from '@/types'
import { useAvailableLocalePath } from '@/composables/useAvailableLocalePath'

interface Props {
  link: NavigationLink
}
const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'change-tab'): void
  (e: 'close-tab', path?: string): void
}>()

const router = useRouter()

const { currentLanguageClass } = useCurrentLanguageClass('navigation-link')
const { availableLocaleRoute } = useAvailableLocalePath()

const comp = computed(() => {
  if (props.link.href || props.link.to) {
    // OR return resolveComponent('NuxtLink')
    return defineNuxtLink({})
  }

  return 'div'
})
const isDisabled = computed(
  () => !(props.link.to || props.link.href) && 'navigation-link--disabled',
)
const linkTarget = computed(() => (props.link.newWindow ? '_blank' : '_self'))

const path = computed(() => {
  if (props.link.href) {
    return props.link.href
  }
  if (props.link.to && props.link.hash) {
    return {
      path: availableLocaleRoute(props.link.to),
      hash: props.link.hash,
    }
  }
  return props.link.to && availableLocaleRoute(props.link.to)
})

const handleKeyDown = (_e: Event) => {
  props.link.to && router.push(props.link.to)
  emit('close-tab')
}

const handleTouch = () => {
  if (comp.value === 'div') {
    return
  }
  const link = props.link.to ?? props.link.href
  emit('close-tab', link)
}

const handleFocusOut = (e: FocusEvent) => {
  const relatedTarget = e.relatedTarget as Element | undefined

  if (relatedTarget && relatedTarget.getAttribute('data-target') === 'true') {
    emit('change-tab')
  }
}
</script>

<template>
  <li role="representation">
    <component
      :is="comp"
      :to="path"
      :class="['navigation-link', isDisabled, currentLanguageClass]"
      :target="linkTarget"
      @click="handleTouch"
      @keydown.enter="handleKeyDown"
      @focusout="handleFocusOut"
    >
      <span v-if="isDisabled" class="sr-only">It's not clickable element</span>
      <div class="navigation-link__container">
        <span v-if="link.title" class="navigation-link__title"
          >{{ $t(`common.navigation.links.${link.title}.title`) }}
          <span v-if="link.additionalInfo" class="navigation-link__pill">{{
            $t('common.navigation.soon')
          }}</span>
        </span>
      </div>
    </component>
  </li>
</template>

<style lang="scss" scoped>
.navigation-link {
  $this: &;
  display: flex;
  align-items: center;
  color: var(--color-text);
  text-decoration: none;
  padding: var(--link-padding-top) var(--link-padding-inline)
    var(--link-padding-bottom);
  position: relative;
  border-radius: var(--border-radius-base);
  gap: 16px;

  &:focus {
    outline: 2px solid var(--cobalt-600);
  }

  &__container {
    display: grid;
    flex: 1 0;
  }

  &__img {
    width: 40px;
    aspect-ratio: 1;
  }

  &__title {
    @include paragraph-s;

    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 21px;
  }

  &__pill {
    font-size: 10px;
    line-height: 16px;
    padding: 2px 8px;
    background-color: var(--pill-bg-color, var(--cobalt-600));
    color: var(--pill-text-color, var(--white));
    border-radius: 8px;
  }

  &:hover {
    @media (min-width: $breakpoint-lg) {
      color: #85858a;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    --pill-bg-color: #f3f4f6;
    --pill-text-color: #112950;

    &:hover {
      --element-bg-color: transparent;
    }
  }

  &--de#{$this}--disabled {
    #{$this}__description {
      width: 90%;
    }
  }
}
</style>
